import React, { useState } from "react"
import styled from "styled-components"
import CategoryIcon from "./CategoryIcon"
import SocialIcons from "./SocialIcons"
import Collapse from "@material-ui/core/Collapse"
import { categories } from "./colors"

const AssetCard = styled.div`
  background-color: white;
  box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.25);
  border-bottom: 16px solid #888;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  ${({ category }) =>
    category &&
    `
      border-bottom-color: ${categories[category]};
    `}

  @media screen and (min-width: 500px) {
    border-bottom-width: 18px;
  }

  > div {
    flex: 1 1 auto;
  }
`

const AssetImage = styled.figure`
  margin: 0;
  position: relative;

  &::before {
    content: "";
    display: block;
    padding-top: 60.8%;
  }

  img {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const AssetDetails = styled.div`
  padding: 0 18px 36px;
  position: relative;

  @media screen and (min-width: 500px) {
    padding: 13px 26px 36px;
  }

  h2 {
    color: black;
    font-size: 1.75rem;
    font-family: "freight-text-pro", serif;
    font-weight: 500;
    line-height: 1;
    margin: 20px 0;

    @media screen and (min-width: 500px) {
      margin-bottom: 6px;
    }

    @media screen and (min-width: 600px) {
      font-size: 2.375rem;
    }

    @media screen and (min-width: 768px) {
      font-size: 2.625rem;
    }
  }

  h3 {
    font-size: 16px;
    font-size: 1rem;
    color: #000;
    font-family: "freight-sans-condensed-pro", sans-serif;
    font-weight: 600;
    line-height: 1;
    margin: 20px 0;

    @media screen and (min-width: 600px) {
      font-size: 18px;
      font-size: 1.125rem;
    }
  }

  p {
    color: black;
    margin-top: 0;
    margin-bottom: 12px;
  }

  > a {
    display: block;
    font-size: 16px;
    font-size: 1rem;
    color: black;
    font-weight: 400;
    margin-bottom: 12px;

    @media screen and (min-width: 600px) {
      font-size: 18px;
      font-size: 1.125rem;
    }
  }

  button {
    appearance: none;
    background: none;
    border-radius: 0;
    padding: 8px;
    position: absolute;
    bottom: 3px;
    right: 8px;
    min-height: 0;
    min-width: 0;
    font-size: 16px;
    font-size: 1rem;
    font-family: "freight-text-pro", serif;
    font-weight: 700;
    color: black;
    transition: color 0.21s eae-out;

    @media screen and (min-width: 500px) {
      font-size: 20px;
      font-size: 1.25rem;
    }

    &:hover,
    &:focus {
      color: #888;
      ${({ category }) =>
        category &&
        `
          color: ${categories[category]}
        `}
    }
  }
`

const Address = styled.p`
  white-space: pre-wrap;
`

const AssetCollapse = styled.div`
  margin-top: 4px;
  border-top: 2px solid #666;
  padding-top: 10px;
  padding-right: 6px;
`

const Transit = styled.div`
  border-bottom: 2px solid #666;
  margin-bottom: 12px;

  h4 {
    margin-top: 0;
    margin-bottom: 11px;
    font-size: 1rem;
    font-family: "freight-sans-condensed-pro", sans-serif;
    font-weight: 700;
    line-height: 1.125;
    text-transform: uppercase;
    color: #333333;

    @media screen and (min-width: 600px) {
      font-size: 1.125rem;
    }

    a {
      color: black;
      transition: color 0.21s ease-out;

      &:hover,
      &:focus {
        text-decoration: none;
        ${({ category }) => `
          color: ${categories[category]};
        `}
      }
    }
  }
`

const Funding = styled.p`
  font-family: "freight-text-pro", serif;
  font-weight: 700;
  color: #111;
  font-size: 22px;
  font-size: 1.375rem;

  span {
    font-family: "freight-sans-condensed-pro", sans-serif;
    text-transform: uppercase;
    font-size: 18px;
    font-size: 1.125rem;
    margin-right: 5px;
  }
`

const FeaturedCircle = styled.a`
  background-color: #e71d13;
  border-radius: 50%;
  display: flex;
  align-items: center;
  text-align: center;
  height: 92px;
  width: 92px;
  position: absolute;
  left: -6px;
  top: -36px;
  color: white;
  font-family: "freight-text-pro", serif;
  font-weight: 700;
  font-size: 16px;
  font-size: 1rem;
  line-height: 1;
  transition: background-color 0.21s ease-out;
  padding-top: 28px;

  @media screen and (min-width: 768px) {
    height: 106px;
    width: 106px;
    left: -6px;
    top: -38px;
    padding-top: 22px;
    font-size: 20px;
    font-size: 1.25rem;
  }

  @media screen and (min-width: 1200px) {
    height: 136px;
    width: 136px;
    padding-top: 38px;
    left: -13px;
    top: -56px;
    font-size: 22px;
    font-size: 1.375rem;
  }

  &:hover,
  &:focus {
    text-decoration: none;
    background-color: #8e1a16;
  }
`

const Asset = (props) => {
  const [open, setOpen] = useState(false)
  const { item, type } = props

  const websiteUrl = (site) => {
    if (!site.match(/http/)) {
      return `http://${site}`
    }
  }

  const googleMapsLink = (address) => {
    const formattedAddress = address.replace(/\W+/gm, "+")
    return `https://www.google.com/maps/dir/?api=1&travelmode=transit&origin=Current+Location&destination=${formattedAddress}`
  }

  const currentGrant = item.amountHistory.reduce(
    (prev, current) => (prev.year > current.year ? prev : current),
    1,
  )

  return (
    <AssetCard category={item.category || "Municipal Support"} id={`asset-${item.id}`}>
      {item.image && (
        <AssetImage>
          <img src={item.image} alt={item.image_alt || item.name} />

          {item.featuredUrl && (
            <FeaturedCircle href={item.featuredUrl}>Featured Asset</FeaturedCircle>
          )}

          {item.category && (
            <CategoryIcon
              category={item.category || "Municipal Support"}
              sports_checkbox={item.sports_checkbox}
            />
          )}
        </AssetImage>
      )}

      <AssetDetails category={item.category || "Municipal Support"}>
        <h2>{item.name}</h2>

        {item.fundedThrough && <h3>{item.fundedThrough}</h3>}

        {item.address && <Address>{item.address}</Address>}

        {item.phone && <a href={`tel:${item.phone.replace(/\D/g, "")}`}>{item.phone}</a>}

        {item.website && (
          <a href={websiteUrl(item.website)} target="_blank" rel="noopener noreferrer">
            {item.website.replace(/http:\/\/|https:\/\/|(\/)$/g, "")}
          </a>
        )}

        <Collapse in={open}>
          <AssetCollapse>
            {item.address && (
              <Transit category={item.category || "Municipal Support"}>
                <h4>
                  <a href={googleMapsLink(item.address)} target="_blank" rel="noopener noreferrer">
                    Transit Line Access
                  </a>
                </h4>
              </Transit>
            )}

            {item.description && (
              <div
                dangerouslySetInnerHTML={{
                  __html: item.description,
                }}
              />
            )}

            {(item.socialMedia || []).length > 0 && (
              <SocialIcons
                links={item.socialMedia}
                category={item.category || "Municipal Support"}
              />
            )}

            {type === "assets" && (
              <div>
                {Intl.NumberFormat("en-EU").format(currentGrant.amount) !== "NaN" && (
                  <Funding>
                    <span>Current Grants:</span>$
                    {Intl.NumberFormat("en-EU").format(currentGrant.amount)} ({currentGrant.year})
                  </Funding>
                )}

                {item.amountGranted && (
                  <Funding>
                    <span>Historical Total:</span>$
                    {Intl.NumberFormat("en-EU").format(item.amountGranted)}
                  </Funding>
                )}

                {item.yearsFunded && (
                  <Funding>
                    <span>Years Funded:</span>
                    {item.yearsFunded}
                  </Funding>
                )}
              </div>
            )}

            {type === "municipal" && (
              <div>
                <Funding>
                  <span>Amount Received:</span>$
                  {Intl.NumberFormat("en-EU").format(currentGrant.amount)} ({currentGrant.year})
                </Funding>

                {item.amountGranted && (
                  <Funding>
                    <span>Historical Total:</span>$
                    {Intl.NumberFormat("en-EU").format(item.amountGranted)}
                  </Funding>
                )}

                {item.yearsFunded && (
                  <Funding>
                    <span>Years Funded:</span>
                    {item.yearsFunded}
                  </Funding>
                )}
              </div>
            )}
          </AssetCollapse>
        </Collapse>

        <button onClick={() => setOpen(!open)}>Show {open ? "Less -" : "More +"}</button>
      </AssetDetails>
    </AssetCard>
  )
}

export default Asset
